import { createContext, useContext, useEffect, type ReactNode } from 'react';

import { useRefreshToken } from '@/api/queries/useAuth';
import { useAuthStore } from '@/auth/useAuthStore';
import type { AuthUser } from '@/types';

interface AuthContextType {
  isAuthenticated: boolean;
  user: AuthUser | null;
  isLoading: boolean;
  login: (user: AuthUser, accessToken: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { isAuthenticated, user, isLoading, setAuth, clearAuth, setLoading } = useAuthStore();
  const refreshTokenMutation = useRefreshToken();

  // Bootstrap authentication on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Only attempt refresh if:
        // 1. We're not on the login page
        // 2. We have a refresh token stored
        const hasRefreshToken = localStorage.getItem('refreshToken');
        
        if (window.location.pathname !== '/login' && hasRefreshToken) {
          await refreshTokenMutation.mutateAsync();
        } else {
          // If we're on login page or no refresh token, just stop loading
          setLoading(false);
        }
      } catch (error) {
        // Silent fail - user will be prompted to login
        // Don't log network errors when backend is not running
        const apiError = error as any;
        if (apiError?.statusCode && apiError.message !== 'No hay token de refresco disponible') {
          console.log('Fallo al restaurar la sesión:', apiError.message);
        }
        setLoading(false);
      }
    };

    // Only run once on mount
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (user: AuthUser, accessToken: string) => {
    setAuth(user, accessToken);
  };

  const logout = () => {
    clearAuth();
    // Redirect to login page
    window.location.href = '/login';
  };

  const contextValue: AuthContextType = {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};